
import { Injectable, inject, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { PushNotificationService } from "./push-notification.service";
import { UserService } from "./user.service";
import { TenantService } from "./tenant.service";
import { TokenStorageService } from "./token-storage.service";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { AuthEventsService } from "./auth-events.service";

@Injectable({
  providedIn: "root"
})
export class StartupPushNotificationService implements OnDestroy {
  private pushNotificationService = inject(PushNotificationService);
  private userService = inject(UserService);
  private tenantService = inject(TenantService);
  private tokenStorage = inject(TokenStorageService);
  private envService = inject(EnvironmentLoaderService);
  private authEventsService = inject(AuthEventsService);

  private isInitialized = false;
  private readonly maxRetries = 3;
  private readonly retryDelay = 2000; // 2 seconds
  private authSubscription?: Subscription;

  constructor() {
    // Listen for authentication events
    this.setupAuthEventListener();
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  /**
   * Setup listener for authentication events
   */
  private setupAuthEventListener(): void {
    this.authSubscription = this.authEventsService.authEvents$.subscribe(event => {
      if (event.type === "LOGIN_COMPLETED") {
        void this.initializeAfterLogin();
      } else if (event.type === "LOGOUT_COMPLETED") {
        this.handleLogout();
      }
    });
  }

  /**
   * Handle logout event
   */
  private handleLogout(): void {
    this.isInitialized = false;
  }

  /**
   * Initialize push notifications only if user is already authenticated at startup
   * This is called during app initialization
   */
  async initializeIfAuthenticated(): Promise<void> {
    try {
      // Check if user is authenticated
      if (!this.isUserAuthenticated()) {
        return;
      }

      await this.initializePushNotifications();

    } catch (error) {
      // Don't throw the error to avoid breaking app initialization
    }
  }

  /**
   * Initialize push notifications after successful login
   * This should be called when the user completes login
   */
  async initializeAfterLogin(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.initializePushNotifications();
    } catch (error) {
      // Don't throw the error to avoid breaking the login flow
    }
  }

  /**
   * Core initialization logic for push notifications
   * This should be called after the user is authenticated and app is stable
   */
  private async initializePushNotifications(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Double-check authentication before proceeding
      if (!this.isUserAuthenticated()) {
        return;
      }

      // Wait for tenant info to be loaded
      await this.ensureTenantInfo();

      // Get user and tenant information
      const userId = this.getUserId();
      const tenantName = this.getTenantName();

      if (!userId || !tenantName) {
        return;
      }

      // Check if push notifications are supported
      if (!this.isPushNotificationSupported()) {
        return;
      }

      // Request permission and subscribe
      await this.subscribeWithRetry(userId, tenantName);

      this.isInitialized = true;

    } catch (error) {
      // Don't throw the error to avoid breaking app/login flow
    }
  }

  /**
   * Check if the user is authenticated
   */
  private isUserAuthenticated(): boolean {
    try {
      const env = this.envService.getEnvConfig();
      const tokenKey = env.loginType === "local" ? env.localTokenKey : env.ssoTokenKey;
      const token = this.tokenStorage.retriveToken(tokenKey);
      
      // Check if token exists and is not expired
      if (!token?.jwtPayload) {
        return false;
      }

      // Check if token is expired
      const expireDate = new Date(token.expireDate);
      const now = new Date();
      
      return expireDate > now;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current user ID
   */
  private getUserId(): string | null {
    try {
      return this.userService.getUserId();
    } catch (error) {
      return null;
    }
  }

  /**
   * Get current tenant name (using name instead of ID as it's available in TenantPublicInfo)
   */
  private getTenantName(): string | null {
    try {
      const tenant = this.tenantService.getCurrentTenant();
      return tenant?.name ?? null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Ensure tenant information is loaded
   */
  private async ensureTenantInfo(): Promise<void> {
      const tenant = this.tenantService.getCurrentTenant();
      if (!tenant) {
        await this.tenantService.loadTenantInfo();
      }
  }

  /**
   * Check if push notifications are supported
   */
  private isPushNotificationSupported(): boolean {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }

  /**
   * Subscribe to push notifications with retry logic
   */
  private async subscribeWithRetry(userId: string, tenantName: string): Promise<void> {
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        // Request notification permission
        const hasPermission = await this.pushNotificationService.requestPermission();
        
        if (!hasPermission) {
          return;
        }

        // Subscribe to notifications
        await this.pushNotificationService.subscribeToNotifications(userId, tenantName);
        
        return;

      } catch (error) {
        
        if (attempt === this.maxRetries) {
          throw new Error(`Failed to subscribe after ${this.maxRetries} attempts: ${error instanceof Error ? error.message : String(error)}`);
        }

        // Wait before retrying
        await this.delay(this.retryDelay * attempt); // Exponential backoff
      }
    }
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Manually retry push notification initialization
   * Can be called from other components if initialization failed
   */
  async retryInitialization(): Promise<void> {
    this.isInitialized = false;
    await this.initializePushNotifications();
  }

  /**
   * Force initialization regardless of current state (for testing/debugging)
   */
  async forceInitialization(): Promise<void> {
    this.isInitialized = false;
    await this.initializePushNotifications();
  }

  /**
   * Check if push notifications are initialized
   */
  get isSubscribed(): boolean {
    return this.isInitialized && this.pushNotificationService.isSubscribed;
  }

  /**
   * Get initialization status
   */
  get initializationStatus(): {
    isInitialized: boolean;
    isAuthenticated: boolean;
    isSubscribed: boolean;
    isPushSupported: boolean;
  } {
    return {
      isInitialized: this.isInitialized,
      isAuthenticated: this.isUserAuthenticated(),
      isSubscribed: this.pushNotificationService.isSubscribed,
      isPushSupported: this.isPushNotificationSupported()
    };
  }
}
